import React, { useEffect, useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Heart, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FavoritesPage = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    fetchFavorites();
  }, [token]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Failed to fetch favorites', error);
      toast.error('Erreur lors du chargement des favoris');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (tutorialId) => {
    try {
      await axios.delete(`${API}/favorites/${tutorialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(favorites.filter(t => t.id !== tutorialId));
      toast.success('Retiré des favoris');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (!user) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pb-24 md:pb-12"
      data-testid="favorites-page"
    >
      {/* Header */}
      <section className="py-12 md:py-20 border-b gold-border">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <Heart className="w-8 h-8 text-primary fill-current" />
            <h1 className="font-serif text-5xl md:text-6xl text-charcoal tracking-tighter">
              Mes Favoris
            </h1>
          </div>
          <p className="text-lg text-taupe">
            {favorites.length} tutoriel{favorites.length !== 1 ? 's' : ''} sauvegardé{favorites.length !== 1 ? 's' : ''}
          </p>
        </div>
      </section>

      {/* Favorites Grid */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-silk h-80 mb-4"></div>
                  <div className="bg-silk h-6 w-3/4 mb-2"></div>
                  <div className="bg-silk h-4 w-1/2"></div>
                </div>
              ))}
            </div>
          ) : favorites.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <Heart className="w-24 h-24 text-taupe mx-auto mb-6" />
              <h2 className="font-serif text-3xl text-charcoal mb-4">Aucun favori pour le moment</h2>
              <p className="text-taupe mb-8">Commencez à sauvegarder vos tutoriels préférés</p>
              <Link
                to="/browse"
                data-testid="favorites-browse-btn"
                className="inline-block bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500"
              >
                Explorer les tutoriels
              </Link>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {favorites.map((tutorial, index) => (
                <motion.div
                  key={tutorial.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative"
                  data-testid={`favorite-card-${tutorial.id}`}
                >
                  <Link to={`/tutorial/${tutorial.id}`}>
                    <div className="overflow-hidden border gold-border mb-4">
                      <div className="relative">
                        <img
                          src={tutorial.thumbnail}
                          alt={tutorial.title}
                          className="w-full h-80 object-cover image-zoom"
                        />
                        <div className="absolute inset-0 cinema-fade opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                          <div className="w-16 h-16 bg-primary/90 flex items-center justify-center">
                            <Play className="w-8 h-8 text-white" fill="white" />
                          </div>
                        </div>
                        {tutorial.is_premium && (
                          <div className="absolute top-4 right-4 bg-primary px-3 py-1">
                            <span className="font-accent text-xs text-white uppercase tracking-wider">Premium</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="font-accent text-xs uppercase tracking-wider text-primary mb-2">
                      {tutorial.category}
                    </p>
                    <h3 className="font-serif text-2xl text-charcoal tracking-tighter group-hover:text-primary transition-colors mb-2">
                      {tutorial.title}
                    </h3>
                    <p className="text-sm text-taupe line-clamp-2">{tutorial.description}</p>
                  </Link>
                  
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      removeFavorite(tutorial.id);
                    }}
                    data-testid={`remove-favorite-${tutorial.id}`}
                    className="absolute top-4 left-4 p-3 bg-secondary/90 hover:bg-secondary text-white transition-colors backdrop-blur-sm"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </motion.div>
  );
};

export default FavoritesPage;
